import { useDebounceFn } from '@vueuse/core';
import { computed, ComputedRef, inject, nextTick, onMounted, Ref, ref, unref, watch } from 'vue';
import type { ComponentRef, Nullable } from '@mts-fe/types';
import { useWindowSizeFn } from '@mts-fe/hooks';
import { getViewportOffset, isBoolean, onElementVisible } from '@mts-fe/utils';
import { FormActionType } from '@mts/components/Form';
import { useModalContext } from '@mts/components/Modal';
import { PageHasFooterKey } from '@mts/enums/pageEnum';
import { adaptiveSize, getScrollHeight, mtsEmitter, windowSize } from '@mts/utils';
import { PCMode } from '@mts/utils/env';
import type { BasicColumn, BasicTableProps, TableRowSelection } from '../types/table';

export function useTableScroll(
  propsRef: ComputedRef<BasicTableProps>,
  tableElRef: Ref<ComponentRef>,
  viewColumnsRef: ComputedRef<BasicColumn[]>,
  rowSelectionRef: ComputedRef<TableRowSelection | undefined>,
  getDataSourceRef: ComputedRef<Recordable[]>,
  wrapRef: Ref<HTMLElement | null>,
  formRef: Ref<FormActionType | null>,
) {
  const pageHasFooter = inject<boolean>(PageHasFooterKey, false);
  const tableHeightRef: Ref<Nullable<number | string>> = ref(
    getScrollHeight(propsRef.value.usedHeight) ?? 167,
  );
  const modalFn = useModalContext();

  // Greater than animation time 280
  const debounceRedoHeight = useDebounceFn(redoHeight, 350);

  const getCanResize = computed(() => {
    const { canResize, scroll } = unref(propsRef);
    return canResize && !(scroll || {}).y;
  });

  watch(
    () => [unref(getCanResize), unref(getDataSourceRef)?.length],
    () => {
      debounceRedoHeight();
    },
    {
      flush: 'post',
    },
  );

  function redoHeight() {
    nextTick(() => {
      calcTableHeight();
    });
  }

  function setHeight(height: number) {
    tableHeightRef.value = height;
    //  Solve the problem of modal adaptive height calculation when the form is placed in the modal
    modalFn?.redoModalHeight?.();
  }

  // No need to repeat queries
  let paginationEl: HTMLElement | null;
  let footerEl: HTMLElement | null;
  let bodyEl: HTMLElement | null;

  /**
   * table wrapper padding 的高度
   * @description 来自于 .vben-basic-table .ant-table-wrapper
   */
  const tableWrapperPadding = 6;

  function handleScrollBar(bodyEl: HTMLElement, tableEl: Element) {
    const hasScrollBarY = bodyEl.scrollHeight > bodyEl.clientHeight;
    const hasScrollBarX = bodyEl.scrollWidth > bodyEl.clientWidth;

    if (hasScrollBarY) {
      tableEl.classList.contains('hide-scrollbar-y') &&
        tableEl.classList.remove('hide-scrollbar-y');
    } else {
      !tableEl.classList.contains('hide-scrollbar-y') && tableEl.classList.add('hide-scrollbar-y');
    }

    if (hasScrollBarX) {
      tableEl.classList.contains('hide-scrollbar-x') &&
        tableEl.classList.remove('hide-scrollbar-x');
    } else {
      !tableEl.classList.contains('hide-scrollbar-x') && tableEl.classList.add('hide-scrollbar-x');
    }
  }

  /**
   * 计算分页器高度
   * @param tableEl table element
   * @returns number
   */
  function calcPaginationHeight(tableEl: Element): number {
    const { pagination, inset } = unref(propsRef);

    let paginationHeight = 0;
    if (!isBoolean(pagination)) {
      // 从 Dom 获取
      if (!paginationEl) {
        paginationEl = tableEl.querySelector('.ant-pagination') as HTMLElement;
      }
      const paginationElMarginBottom = inset ? 0 : 8;
      if (paginationEl) {
        // 分页 margin-top
        const paginationElMarginTop = parseInt(getComputedStyle(paginationEl)?.marginTop) || 8 + 22;
        // 分页高度
        const offsetHeight = paginationEl.offsetHeight;
        paginationHeight = offsetHeight + paginationElMarginTop + paginationElMarginBottom;
      } else {
        // 找不到分页组件，缺省给予默认分页 margin-top + 高度 + margin-bottom
        paginationHeight = 8 + 22 + paginationElMarginBottom;
      }
    } else {
      // 不显示分页，pagination 为 false 的时候
      paginationHeight = 0;
    }
    return paginationHeight;
  }

  function calcFooterHeight(tableEl: Element): number {
    let footerHeight = 0;
    if (!footerEl) {
      footerEl = tableEl.querySelector('.ant-table-footer') as HTMLElement;
    } else {
      const offsetHeight = footerEl.offsetHeight;
      footerHeight += offsetHeight || 0;
    }
    return footerHeight;
  }

  function calcHeaderHeight(headEl: Element | null): number {
    let headerHeight = 0;
    if (headEl) {
      headerHeight = (headEl as HTMLElement).offsetHeight;
    }
    return headerHeight;
  }

  /**
   * 计算从表头一直到body底部的总高度
   * @param tableEl table element
   * @param headEl table 页头 element
   * @returns number
   */
  function calcBottomAndPaddingHeight(tableEl: Element, headEl: Element) {
    const { isCanResizeParent } = unref(propsRef);
    let bottomIncludeBody = 0;
    if (unref(wrapRef) && isCanResizeParent) {
      // 继承父元素高度
      const wrapHeight = unref(wrapRef)?.offsetHeight ?? 0;

      let formHeight = unref(formRef)?.$el?.offsetHeight ?? 0;
      if (formHeight) {
        // 来自于 .vben-basic-table-form-container .ant-form 以及 .vben-basic-table-form-container
        formHeight += 0 + 6 * 2;
      }

      bottomIncludeBody = wrapHeight - getMarginPaddingHeight() - formHeight;
    } else {
      // 缺省 wrapRef 情况下
      bottomIncludeBody = getViewportOffset(headEl).bottomIncludeBody;
    }

    return bottomIncludeBody;
  }

  /**
   * 计算 table 在 modal 内 modal 所占用的高度
   * @param tableEl table element
   * @returns number
   */
  function calcModalHeight(tableEl: Element) {
    // 找一下 table 是否在 modal 内，获得 modal、wrap、footer，并考虑 fullscreen 的情况
    let modalEl: Nullable<HTMLElement> = null;
    let modalFooterEl: Nullable<HTMLElement> = null;
    let modalElIterator: HTMLElement = tableEl.parentElement!;
    let modalIsFullscreen = false;
    while (modalElIterator !== (document.fullscreenElement || document.body)) {
      if (!modalElIterator) break;
      if (modalElIterator.classList.contains('ant-modal')) {
        modalEl = modalElIterator;
        // 兼容弹窗内有嵌套弹窗的情况
        const footerEls = modalEl.querySelectorAll('.ant-modal-content>.ant-modal-footer');
        modalFooterEl = footerEls[footerEls.length - 1] as HTMLElement;
        modalIsFullscreen = modalEl.parentElement?.classList.contains('fullscreen-modal') ?? false;
        break;
      }
      modalElIterator = modalElIterator.parentElement!;
    }

    if (modalEl) {
      // table 在 modal 内

      // modal top
      const { top: modalTop = 0 } = modalEl ? getViewportOffset(modalEl) : {};

      // 来自于 .ant-modal，非全屏为 24，全屏为 0
      const modalBottom = modalIsFullscreen ? 0 : 24;

      //  modal footer 高度
      const modalFooterHeight = modalFooterEl?.offsetHeight ?? 0;

      // modal footer 边距，来自于 .ant-modal .ant-modal-footer
      const modalFooterMarginTop = modalFooterEl
        ? modalIsFullscreen
          ? 0
          : parseInt(getComputedStyle(modalFooterEl).marginTop)
        : 0;

      // 来自于 .ant-modal .ant-modal-body > .scrollbar
      const modalScrollBarHeight = 4;
      return (
        (modalTop > modalBottom ? modalTop : modalBottom) +
        modalFooterHeight +
        modalFooterMarginTop +
        modalScrollBarHeight
      );
    }

    // table 不住 modal 内
    return 0;
  }

  /**
   * 根据样式返回一些间距高度
   * @returns number
   */
  function getMarginPaddingHeight() {
    const { isCanResizeParent, inset } = unref(propsRef);

    if (inset) return 0;

    if (unref(wrapRef) && isCanResizeParent) {
      // 继承父元素高度
      return tableWrapperPadding;
    }
    return (
      tableWrapperPadding + 0 // 来自于 .vben-basic-table-form-container 或是 .p-4
    );
  }

  async function calcTableHeight() {
    const { usedHeight, resizeHeightOffset, maxHeight, minHeight } = unref(propsRef);

    const table = unref(tableElRef);
    if (!table) return;

    const tableEl: HTMLElement = table.$el;
    if (!tableEl || tableEl.nodeType !== Node.ELEMENT_NODE) return;

    // 兼容初始化时不可见的场景
    if (PCMode && tableEl.offsetHeight <= 0) {
      onElementVisible(tableEl, calcTableHeight);
      return;
    }

    if (!bodyEl) {
      bodyEl = tableEl.querySelector('.ant-table-body');
      if (!bodyEl) return;
    }
    if (usedHeight) {
      const height = getScrollHeight(usedHeight)!;
      bodyEl!.style.height = height + 'px';
      setHeight(height);
      return;
    }

    handleScrollBar(bodyEl, tableEl);

    bodyEl!.style.height = 'unset';
    const pageFooterHeight = pageHasFooter ? adaptiveSize(60 + 8) : 0;

    const tableData = unref(getDataSourceRef);
    if (!unref(getCanResize) || !unref(tableData)) return;

    await nextTick();
    // Add a delay to get the correct bottomIncludeBody paginationHeight footerHeight headerHeight

    const headEl = tableEl.querySelector('.ant-table-thead ');

    // console.log(headEl, 'headEl', unref(viewColumnsRef).length);
    if (!headEl || unref(viewColumnsRef).length <= 1) return;

    const paginationHeight = tableData.length === 0 ? 0 : calcPaginationHeight(tableEl);
    const footerHeight = calcFooterHeight(tableEl);
    const headerHeight = calcHeaderHeight(headEl);
    const bottomIncludeBody = calcBottomAndPaddingHeight(tableEl, headEl);

    const modalHeight = calcModalHeight(tableEl);

    const marginPaddingHeight = getMarginPaddingHeight();

    // Math.floor 宁愿小1px，也不溢出
    let height = Math.floor(
      bottomIncludeBody -
        (resizeHeightOffset || 0) -
        pageFooterHeight -
        paginationHeight -
        footerHeight -
        headerHeight -
        // 弹窗（如果有）相关高度
        modalHeight -
        // 样式间距高度
        marginPaddingHeight -
        // 预留非整数高度溢出（如实际高度为100.5，offsetHeight 的值为101）
        1,
    );
    height = tableData.length === 0 && modalHeight ? 208 : height;

    height = (height > maxHeight! ? (maxHeight as number) : height) ?? height;
    if (minHeight) {
      height = height < minHeight ? minHeight : height;
    }
    setHeight(height);

    !PCMode &&
      console.error(`请指定 :usedHeight="${windowSize.height - height - 1}" ，并移除canResize`);
    bodyEl!.style.height = `${height}px`;
  }
  PCMode && useWindowSizeFn(calcTableHeight, { wait: 280 });
  onMounted(() => {
    calcTableHeight();
    nextTick(() => {
      debounceRedoHeight();
    });

    mtsEmitter.on('tableWidthChanged', debounceRedoHeight);
  });

  const getScrollX = computed(() => {
    let width = 0;
    if (unref(rowSelectionRef)) {
      width += 60;
    }

    // TODO props ?? 0;
    const NORMAL_WIDTH = 120;

    const columns = unref(viewColumnsRef);
    columns.forEach((item) => {
      width += Number.parseFloat(item.width as string) || 0;
    });

    const unsetWidthNum = columns.filter((item) => !Reflect.has(item, 'width')).length;
    if (unsetWidthNum !== 0) {
      width += unsetWidthNum * NORMAL_WIDTH;
    }

    const table = unref(tableElRef);
    const tableWidth = table?.$el?.offsetWidth ?? 0;
    return tableWidth > width ? '100%' : width;
  });

  const getScrollRef = computed(() => {
    const tableHeight = unref(tableHeightRef);
    const { canResize, scroll } = unref(propsRef);
    return {
      x: unref(getScrollX),
      y: canResize ? tableHeight : null,
      scrollToFirstRowOnChange: false,
      ...scroll,
    } as BasicTableProps['scroll'];
  });

  return { getScrollRef, redoHeight };
}
